import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { CmsSectionShell } from './CmsSectionShell.tsx';
import { CmsSectionState } from './CmsSectionState.tsx';
import { themeOf, EYEBROW, BUTTON_FILL, OUTLINE_CONTROL, type ThemeClasses } from './cmsTheme.ts';
import { useFestivalData } from '../../lib/festivalData.ts';
import { cdnVariant, cdnSrcSet } from '../../lib/cdn.ts';
import { newsStoryPath } from '../../lib/pages.ts';
import { formatNewsDate, getNewsArchiveModel } from '../../application/festivalSelectors.ts';
import type { NewsItem, NewsSection } from '../../lib/types.ts';

/** Articles per page in the full desktop list — a 3x3 grid. */
const PAGE_SIZE = 9;

/**
 * Lists the festival news articles (from news.json). Each card links to the dedicated news-story
 * page at /nyheder/:id.
 *
 * Layout:
 * - Mobile: a horizontal swipe carousel showing the current card plus a peek of the next, with
 *   round prev/next arrows (like the gallery) — keeps the page short instead of a long stack.
 * - Desktop (md+): a grid. In the full variant it paginates (PAGE_SIZE per page with a pager);
 *   in the teaser variant (`maxItems`) it shows only the newest N plus a "Se alle nyheder" link.
 *
 * In v2 the "see all" link is a quiet outline pill in a header row above the grid rather than a
 * coral pill centered under it: it is navigation away from the block, not the block own CTA.
 */
export function CmsNewsSection({ theme, guitar, maxItems }: NewsSection) {
  const t = themeOf(theme);
  const { data, loading, error } = useFestivalData();
  const allItems = data ? getNewsArchiveModel(data).items : [];
  const limited = typeof maxItems === 'number' && maxItems > 0;

  const [page, setPage] = useState(1);
  const topRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const pageCount = limited ? 1 : Math.max(1, Math.ceil(allItems.length / PAGE_SIZE));
  // Clamp defensively in case the feed shrank since the last render.
  const currentPage = Math.min(page, pageCount);

  // Desktop: teaser slice, or the current page of the full list.
  let desktopItems = allItems;
  if (limited) {
    desktopItems = allItems.slice(0, maxItems);
  } else if (pageCount > 1) {
    desktopItems = allItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  }

  // Mobile carousel: the teaser slice, or the whole archive (swipe replaces pagination on mobile).
  const carouselItems = limited ? desktopItems : allItems;

  const showAllLink = limited && allItems.length > desktopItems.length;
  const showPager = !limited && pageCount > 1;
  const showArrows = carouselItems.length > 1;

  const goToPage = (next: number) => {
    setPage(next);
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollCarousel = (direction: 1 | -1) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-news-card]');
    const amount = (card?.offsetWidth ?? el.clientWidth * 0.85) + 16;
    el.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      <CmsSectionState
        loading={loading}
        error={!!error}
        loadingLabel="Indlæser nyheder…"
        bodyClass={t.body}
        errorMessage="Nyhederne kunne ikke indlæses."
      >
        {showAllLink && (
          <div className="mb-6 md:mb-8 flex justify-end">
            <Link
              to="/nyheder"
              className={`inline-flex items-center gap-2 rounded-full px-5 md:px-6 py-2.5 md:py-3 font-medium text-sm md:text-base transition-colors ${OUTLINE_CONTROL[t.outline]}`}
            >
              Se alle nyheder
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        )}

        {/* Desktop: grid (paginated in the full variant) */}
        <div ref={topRef} className="hidden md:grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">
          {desktopItems.map(item => (
            <NewsCard key={item.id} item={item} t={t} />
          ))}
        </div>

        {/* Mobile: swipe carousel — current card + a peek of the next, round arrows to page */}
        <div className="md:hidden relative">
          <div
            ref={carouselRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 scrollbar-none [&::-webkit-scrollbar]:hidden"
          >
            {carouselItems.map(item => (
              <div key={item.id} data-news-card className="shrink-0 w-[85%] snap-start">
                <NewsCard item={item} t={t} />
              </div>
            ))}
          </div>

          {showArrows && (
            <>
              <button
                type="button"
                onClick={() => scrollCarousel(-1)}
                aria-label="Forrige nyhed"
                className="absolute left-1 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-mf-yellow text-mf-black shadow-md active:scale-95 transition-transform"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => scrollCarousel(1)}
                aria-label="Næste nyhed"
                className="absolute right-1 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-mf-yellow text-mf-black shadow-md active:scale-95 transition-transform"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>

        {showPager && (
          <nav className="mt-10 md:mt-12 hidden md:flex items-center justify-center gap-2" aria-label="Nyhedssider">
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Forrige side"
              className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors ${OUTLINE_CONTROL[t.outline]} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                type="button"
                onClick={() => goToPage(n)}
                aria-label={`Side ${n}`}
                aria-current={n === currentPage ? 'page' : undefined}
                className={`inline-flex h-10 min-w-10 items-center justify-center rounded-full px-3 font-medium transition-colors ${
                  n === currentPage ? BUTTON_FILL[t.button] : OUTLINE_CONTROL[t.outline]
                }`}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === pageCount}
              aria-label="Næste side"
              className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors ${OUTLINE_CONTROL[t.outline]} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </nav>
        )}
      </CmsSectionState>
    </CmsSectionShell>
  );
}

/** One news article card — a full-height link with a 3:2 image over the section card shade. */
function NewsCard({ item, t }: { item: NewsItem; t: ThemeClasses }) {
  return (
    <Link
      to={newsStoryPath(item)}
      className="group flex flex-col h-full rounded-card overflow-hidden"
    >
      {item.imageUrl && (
        <div className={`aspect-3/2 w-full overflow-hidden ${t.img}`}>
          <img
            src={cdnVariant(item.imageUrl, 'mobile')}
            srcSet={cdnSrcSet(item.imageUrl) || undefined}
            sizes="(min-width: 1024px) 360px, (min-width: 640px) 50vw, 100vw"
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        </div>
      )}
      <div className={`${t.card.bg} ${t.card.text} p-5 md:p-6 flex flex-1 flex-col`}>
        <div className={`${EYEBROW} ${t.eyebrow} mb-2`}>
          {formatNewsDate(item.publishDate)}
        </div>
        <h3 className="font-display text-[22px] md:text-2xl leading-tight text-balance">
          {item.title}
        </h3>
        <span className="mt-auto pt-4 inline-flex items-center gap-2 font-medium">
          Læs mere
          <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </span>
      </div>
    </Link>
  );
}
